import { EmeraldError } from 'src/common/api/emerald-error';
import { showEmeraldDialog } from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { popEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { VerticalAlignment } from 'src/common/enumerations/verticalAlignment';
import {
  CommunicationError,
  CustomerNotFound,
  SessionExpired,
  TenderAmountExceededEligibilityAmount,
} from 'src/common/helpers/error-code';
import { ICustomer } from 'src/common/interfaces/dtos/ICustomer';
import { ICustomerSearchCriteria } from 'src/common/interfaces/dtos/ICustomerSearchCriteria';
import { IMessageModal, MessageModalSizeEnum } from 'src/common/interfaces/dtos/IMessageModal';
import { IPagedData } from 'src/common/interfaces/dtos/IPagedData';
import { showTenderAmountEligibilityModalCustomerSearchModal } from 'src/price-override/business-logic/handle-price-override';
import {
  closeCustomerLoyaltySearch,
  showCustomerLoyaltySearch,
} from '../components/customer-loyalty-search';
import { searchCustomersByCriteria } from './search-customers';
import { setLoyaltyCustomer } from './set-customer';
import { popupAsyncMessage } from '../../messages/business-logic/popup-message';
import { AbortedError } from 'src/common/api/extended-emerald-error';

export const handleCustomerLoyaltySearch = (): void => {
  const onClose = closeCustomerLoyaltySearch;

  const onSubmit = async (customer: ICustomer): Promise<void> => {
    return setLoyaltyCustomer(customer, 'Keyed', false)
      .then(closeCustomerLoyaltySearch)
      .catch(err => {
        if (err instanceof AbortedError) {
          closeCustomerLoyaltySearch();
          return;
        }
        if (err.code === TenderAmountExceededEligibilityAmount) {
          return showTenderAmountEligibilityModalCustomerSearchModal();
        }
        onCustomerNotFoundError(err);
      });
  };

  const onSearch = async (
    searchCriteria: Partial<ICustomerSearchCriteria>,
    pageNumber: number,
  ): Promise<IPagedData<ICustomer>> => {
    let customerDisplay: IPagedData<ICustomer> = {
      lastPage: true,
      totalPages: 0,
      pageNumber: 0,
      totalResults: 0,
      pageContent: [],
    };
    await withSpinner(async () => {
      await searchCustomersByCriteria(searchCriteria, pageNumber)
        .then((pagedData: IPagedData<ICustomer>) => {
          customerDisplay = {
            ...pagedData,
          };
        })
        .catch(async err => {
          if (err.code === SessionExpired) {
            await popupAsyncMessage(err);
            closeCustomerLoyaltySearch();
          } else {
            onCustomerNotFoundError(err);
          }
        });
    });
    return customerDisplay;
  };

  showCustomerLoyaltySearch({
    onClose,
    onSearch,
    onSubmit,
  });
};

const closeAndExitCustomerLoyalty = (): void => {
  popEmeraldModal();
  closeCustomerLoyaltySearch();
};

export const onCustomerNotFoundError = (err: EmeraldError): void => {
  const { subMessage, message, acceptButtonText } = parseCustomerLoyaltyErrorMessage(err);
  const messageProps: IMessageModal = {
    type: EmeraldDialogTypes.Error,
    message: message,
    subMessage: subMessage,
    onAccept: popEmeraldModal,
    onCancel: closeAndExitCustomerLoyalty,
    disableBackdropClick: true,
    acceptButtonLabel: acceptButtonText,
    closeButtonLabel: 'Cancel',
    size: MessageModalSizeEnum.Medium,
    verticalAlignment: VerticalAlignment.Center,
  };
  showEmeraldDialog(messageProps);
};

export const parseCustomerLoyaltyErrorMessage = (
  error: EmeraldError,
): { message: string; subMessage: string; acceptButtonText: string } => {
  switch (error.code) {
    case CustomerNotFound: {
      return {
        message: 'TitleNoLoyaltyCustomerFound',
        subMessage: 'MessageNoLoyaltyCustomerFound',
        acceptButtonText: 'Ok',
      };
    }
    case CommunicationError: {
      return {
        message: 'TitleLoyaltyServerOffline',
        subMessage: 'MessageLoyaltyServerOffline',
        acceptButtonText: 'CheckScanner_Retry',
      };
    }

    default: {
      return {
        message: error.message,
        subMessage: '',
        acceptButtonText: 'Ok',
      };
    }
  }
};
